import type { SelectOptionType } from './types';

/**
 * Greedy-fit the selected labels into the char budget; the first N labels that
 * fit FULLY become visible tags. The remainder is implicitly antd's overflow
 * chip ("+(total - shown)"). Reserves room for the trailing "+M" so it never
 * pushes the last visible tag out.
 *
 * Degenerate case: when even the first label exceeds the budget, force at
 * least one visible tag — otherwise the field collapses to a bare "+N" chip
 * with no context for what's selected.
 *
 * Must be computed from the value the control is DISPLAYING (see
 * `MultiSelectProps.maxDisplayChars`): deriving it from an upstream store
 * value desyncs the budget from the rendered tags whenever `onChange` is
 * debounced, collapsing the fresh selection into a bare "+N" chip until the
 * debounce flushes.
 */
export function computeVisibleTagCount(
  values: Array<string | number>,
  options: SelectOptionType[],
  maxChars: number,
): number {
  const total = values.length;
  if (total === 0) return 0;

  let shown = 0;
  let joinedLen = 0;
  for (let i = 0; i < total; i++) {
    const opt = findOption(options, values[i]);
    const label = String(opt?.label ?? values[i]);
    const candidateLen = i === 0 ? label.length : joinedLen + 2 + label.length;
    const remainingAfter = total - i - 1;
    const suffixLen = remainingAfter > 0 ? ` +${remainingAfter}`.length : 0;
    if (candidateLen + suffixLen <= maxChars) {
      joinedLen = candidateLen;
      shown = i + 1;
    } else {
      break;
    }
  }
  return Math.max(shown, 1);
}

function findOption(
  options: SelectOptionType[],
  value: string | number,
): SelectOptionType | undefined {
  for (const o of options) {
    if (o.value === value) return o;
    if (o.options) {
      const sub = findOption(o.options, value);
      if (sub) return sub;
    }
    if (o.children) {
      const sub = findOption(o.children as SelectOptionType[], value);
      if (sub) return sub;
    }
  }
  return undefined;
}
